// ═══════════════════════════════════════════════════════════════
//  HomePage v9 — page de garde de l'EPJ App Globale
//  - Tuiles avec badges de notification (retards outils, avancement)
//  - Bannière rappel avancement le 20+ du mois
// ═══════════════════════════════════════════════════════════════
import { useMemo, useState, useEffect } from "react";
import { collection, query, where, onSnapshot, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase";
import { EPJ, font, radius, space, fontSize, fontWeight } from "../core/theme";
import { Banner } from "../core/components/Banner";
import { Badge } from "../core/components/Badge";
import { useViewport } from "../core/useViewport";
import { useAuth } from "../core/AuthContext";
import { useData } from "../core/DataContext";
import { can, getEffectiveRolePerms } from "../core/permissions";
import { canSeeDashboards } from "../core/dashboardsAccess";
import {
  computeParcNotifications, computeAvancementNotifications,
  computeReservesNotifications, computeTachesNotifications,
  isRappelAvancementActif, currentMonthLabel,
} from "../core/notificationsUtils";
// v10.J — bannière "commandes en retard" basée sur la date pertinente
import { isOrderLate } from "../modules/commandes/orderDates";
import { hasUnreadMessages } from "../modules/commandes/orderMessages";
// RH-badge — file de validation congés (N1 conducteur / N2 direction)
import { resourcesForConductor, isMyChantier as estAffecteChantier } from "../modules/planning/planningModel";
// T4 — badge/bannières Tâches. `ROLES_PILOTES` et `chantierIdsDepuisFiches`
// sont importés de leurs modules SANS import (testés en node pur) : la règle
// « qui valide » ne doit exister qu'à un seul endroit.
import { ROLES_PILOTES } from "../modules/taches/workflow";
import { chantierIdsDepuisFiches, decouperIn } from "../modules/taches/affectation";
import { rappelsUtilisateur, etatEcheance } from "../modules/chiffrage/offresModel.js";
import { poserFocus } from "../modules/taches/focusTache";
import { stockageNavigateur } from "../modules/taches/replis";
// Interventions — modèle PUR (sans Firebase) : le module lui-même reste lazy.
import { notificationsInterventions, roleClaim, peutPlanifier, memoriserOnglet, ONGLET_A_PLANIFIER } from "../modules/interventions/interventionsModel";
// Mêmes requêtes que la liste (Firestore seul, sans Storage ni callable).
import { listerInterventions } from "../modules/interventions/interventionsLecture";
import { notificationReaffectation } from "../modules/interventions/interventionsPlanning";
import { ancreAvecId } from "../core/routes";

// 5 modules métier
const MODULES_META = [
  {
    id: "commandes",
    title: "Commandes",
    subtitle: "Matériel et équipement",
    icon: "📦",
    accent: EPJ.blue,
    enabled: true,
  },
  {
    id: "avancement",
    title: "Avancement chantier",
    subtitle: "Progression par tâche et bâtiment",
    icon: "📊",
    accent: EPJ.green,
    enabled: true,
  },
  {
    id: "parc-machines",
    title: "Parc machines",
    subtitle: "Outillage et véhicules",
    icon: "🔧",
    accent: EPJ.orange,
    enabled: true,
  },
  {
    id: "reserves",
    title: "Réserves & quitus",
    subtitle: "Suivi SAV & garantie",
    icon: "📝",
    accent: EPJ.catEtude,
    enabled: true,
  },
  {
    id: "gestionChantier",
    title: "Gestion de chantier",
    subtitle: "Chantiers, onglets & suivi",
    icon: "🏗️",
    accent: EPJ.gray700,
    enabled: true,
  },
];

// Tuile Planning ressources (L8) — page indépendante (route module:planning),
// gatée par le droit rh.planning. Distincte de l'onglet planning d'un chantier.
const PLANNING_TILE = {
  id: "planning",
  title: "Planning",
  subtitle: "Affectation des équipes",
  icon: "📆",
  accent: EPJ.catEtude,
  enabled: true,
};

// Tuile Ressources humaines (RH-1) — page indépendante (route module:rh),
// gatée par le droit rh (module RH). Onglets internes gatés par sous-clé.
const RH_TILE = {
  id: "rh",
  title: "Ressources humaines",
  subtitle: "Congés & absences",
  icon: "👥",
  accent: EPJ.catCourantFaible,
  enabled: true,
};

// Tuile Tâches (T2) — page indépendante (route module:taches), gatée par la
// clé `taches`. Voie B STRICTE : cette clé vit uniquement dans `rolesConfig`,
// jamais dans src/core/permissions.js (trio sensible, CLAUDE.md §12). Tant
// qu'elle n'y est pas écrite, can() renvoie false et la tuile n'apparaît pas.
// ⚠ Volontairement HORS de MODULES_META : `visibleModules` est réservé aux
// 5 modules métier historiques.
const TACHES_TILE = {
  id: "taches",
  title: "Tâches",
  subtitle: "À faire et restes à faire",
  icon: "✅",
  accent: EPJ.green,
  enabled: true,
};

// Tuile Carnet de matériel — page indépendante (route module:carnetMateriel),
// gatée par la clé `carnetMateriel`. Même régime que TACHES_TILE : Voie B
// STRICTE (rolesConfig seul, jamais permissions.js), hors MODULES_META.
const CARNET_MATERIEL_TILE = {
  id: "carnetMateriel",
  title: "Carnet de matériel",
  subtitle: "Carnets chantier & catalogue",
  icon: "📘",
  accent: EPJ.blue,
  enabled: true,
};

// Tuile Interventions — page indépendante (route module:interventions),
// gatée par la clé `interventions`. Même régime que TACHES_TILE : Voie B
// STRICTE (rolesConfig seul, jamais permissions.js), hors MODULES_META.
const INTERVENTIONS_TILE = {
  id: "interventions",
  title: "Interventions",
  subtitle: "Demandes clients & dépannages",
  icon: "🛠️",
  accent: EPJ.urgent,
  enabled: true,
};

const CHIFFRAGE_TILE = {
  id: "chiffrage", title: "Chiffrage",
  subtitle: "Marchés, TMA & catalogue de prix", icon: "🧮",
  accent: EPJ.catEtude, enabled: true,
};

const DASHBOARD_TILE = {
  id: "dashboard",
  title: "Dashboard",
  subtitle: "Vue de pilotage",
  icon: "📊",
  accent: EPJ.blue,
  enabled: true,
};

// Collection Dashboards (Module Commande étape 4) — page indépendante,
// distincte de la tuile "Dashboard" ci-dessus (dashboards direction/public).
const COLLECTION_DASHBOARDS_TILE = {
  id: "collection-dashboards",
  title: "Collection Dashboards",
  subtitle: "Dashboard achat & pilotage",
  icon: "🧾",
  accent: EPJ.blue,
  enabled: true,
};

export function HomePage({ onOpenModule, onOpenDashboard, onOpenCollectionDashboards }) {
  const { user } = useAuth();
  const isMobile = useViewport() === "mobile";
  const { rolesConfig, outillageSorties, avancementValidations, chantiers, reserves, commandes, users, featureFlags = {} } = useData();
  if (!user) return null;

  const visibleModules = MODULES_META.filter(m => {
    // Le module Réserves utilise la clé permissions "reserves-quitus"
    const permKey = m.id === "reserves" ? "reserves-quitus" : m.id;
    return can(user, permKey, "_access", rolesConfig);
  });
  const showDashboard =
    can(user, "_dashboards", "direction", rolesConfig) ||
    can(user, "_dashboards", "conducteur", rolesConfig) ||
    can(user, "_dashboards", "public", rolesConfig);

  const showPlanning = can(user, "rh.planning", "_access", rolesConfig);
  const showRH = can(user, "rh", "_access", rolesConfig);
  const showTaches = can(user, "taches", "_access", rolesConfig);
  const showChiffrage = can(user, "chiffrage", "_access", rolesConfig) === "all" && can(user, "chiffrage", "view", rolesConfig) === "all";
  const showCarnetMateriel = can(user, "carnetMateriel", "_access", rolesConfig);
  const showInterventions = can(user, "interventions", "_access", rolesConfig);
  // Accès au module Parc machines : conditionne aussi la bannière "outils en retard"
  // (sinon un monteur sans accès Parc verrait la bannière et pourrait ouvrir le module).
  const canParc = can(user, "parc-machines", "_access", rolesConfig);

  const [offresRappel, setOffresRappel] = useState([]);
  const [horlogeRappel, setHorlogeRappel] = useState(() => new Date());
  useEffect(() => {
    if (!showChiffrage || !user?.uid) { setOffresRappel([]); return; }
    let actif = true;
    setOffresRappel([]);
    import("../modules/chiffrage/offresDonnees.js").then(m => m.listerOffres())
      .then(ds => { if (actif) setOffresRappel(ds); })
      .catch(() => { if (actif) setOffresRappel([]); });
    const timer = setInterval(() => setHorlogeRappel(new Date()), 60000);
    return () => { actif = false; clearInterval(timer); };
  }, [showChiffrage, user?.uid]);
  const mesRemises = rappelsUtilisateur(offresRappel, user?.uid, horlogeRappel);
  const allTiles = [
    ...visibleModules,
    ...(showPlanning ? [PLANNING_TILE] : []),
    ...(showTaches ? [TACHES_TILE] : []),
    ...(showChiffrage ? [CHIFFRAGE_TILE] : []),
    ...(showCarnetMateriel ? [CARNET_MATERIEL_TILE] : []),
    ...(showInterventions ? [INTERVENTIONS_TILE] : []),
    ...(showRH ? [RH_TILE] : []),
    ...(showDashboard ? [DASHBOARD_TILE] : []),
    ...(canSeeDashboards(user) ? [COLLECTION_DASHBOARDS_TILE] : []),
  ];

  // ─── Curation des tuiles d'accueil par rôle (additif, donnée pure) ───
  //   rolesConfig/{role}._homeTiles[tileId] === false → tuile masquée à l'accueil.
  //   Absent ou true = visible (défaut). N'enlève JAMAIS l'accès au module :
  //   épure seulement la page d'accueil. Géré dans Admin → "Visibilité accueil".
  const primaryRole = user?.role || user?.roles?.[0] || null;
  const eff = primaryRole ? getEffectiveRolePerms(primaryRole, rolesConfig) : {};
  const homeTiles = eff?._homeTiles || {};
  const curatedTiles = allTiles.filter(t => homeTiles[t.id] !== false);

  // ─── Congés à valider (RH-badge) — listener CIBLÉ : n'ouvre rien pour un
  //     non-validateur. Lecture SEULE de `conges`, filtrage N1/N2 côté client. ───
  const validerScope = can(user, "rh.conges", "validate", rolesConfig);
  const [congesEnCours, setCongesEnCours] = useState([]);
  useEffect(() => {
    if (!validerScope) { setCongesEnCours([]); return; }
    const q = query(collection(db, "conges"), where("statut", "in", ["DEMANDE", "VALIDEE_N1"]));
    const unsub = onSnapshot(
      q,
      (snap) => setCongesEnCours(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setCongesEnCours([]),
    );
    return unsub;
  }, [validerScope]);

  // ─── T4 — Tâches : compteur d'accueil (getDocs PONCTUEL) ───
  // 🔴 PAS d'onSnapshot ici, contrairement au bloc congés au-dessus. La
  // collection `taches` est écrite par la PWA et par le module ; un listener
  // de plus au boot est exactement ce que la contrainte PERF-1 interdit. Le
  // compteur se rafraîchit au prochain retour sur l'accueil, ce qui suffit
  // pour une pastille.
  const [tachesAssignees, setTachesAssignees] = useState([]);
  const [tachesAValider, setTachesAValider] = useState([]);
  useEffect(() => {
    if (!showTaches || !user?.uid) {
      setTachesAssignees([]); setTachesAValider([]);
      return undefined;
    }
    let annule = false;
    (async () => {
      const ref = collection(db, "taches");
      // (1) Ce qui M'EST nommément assigné — pour tout le monde.
      const requetes = [getDocs(query(ref, where("assigneUid", "==", user.uid)))];

      // (2) Ce qui attend MA validation — pilotes seulement.
      // Périmètre DÉLIBÉRÉMENT réduit aux fiches chantier, sans la source
      // `planningCreneaux` que le module Tâches, lui, consulte : la bannière
      // « à valider » vise les PILOTES, dont le lien au chantier passe par
      // `conducteurId` / `chefChantierIds`, pas par un créneau de planning.
      // Zéro lecture supplémentaire — `chantiers` est déjà dans le
      // DataContext — et l'accueil n'a pas à payer une requête planning.
      const estPilote = ROLES_PILOTES.includes(primaryRole || "");
      if (estPilote) {
        const mesIds = [...chantierIdsDepuisFiches(chantiers, user, estAffecteChantier)];
        for (const paquet of decouperIn(mesIds)) {
          // ⚠ LES DEUX `where` SONT OBLIGATOIRES. Une requête sur le seul
          // `aValiderConducteur` est REFUSÉE par les rules : aucune branche
          // du `allow read` n'est démontrable sans `chantierId` ou
          // `assigneUid` épinglé par un filtre. L'index composite
          // (chantierId, aValiderConducteur) est déployé depuis T1.
          requetes.push(getDocs(query(
            ref,
            where("chantierId", "in", paquet),
            where("aValiderConducteur", "==", true),
          )));
        }
      }

      // (3) Ce que J'AI confié et qui attend MA validation.
      // Hors du garde `estPilote` À DESSEIN : sur une tâche SANS chantier, la
      // branche pilote des rules ne s'ouvre pas (elle exige estPartagee()) et
      // c'est le CRÉATEUR qui valide — quel que soit son rôle. Sans cette
      // requête, le cas « tâche attribuée à une personne, sans chantier »
      // n'émet aucun signal quand l'assigné coche « fait ».
      // Rules : la branche estProprietaire() est démontrable, `ownerUid` étant
      // épinglé par le filtre. Deux égalités sans orderBy → servi par fusion
      // d'index simples, aucun index composite requis.
      requetes.push(getDocs(query(
        ref,
        where("ownerUid", "==", user.uid),
        where("aValiderConducteur", "==", true),
      )));

      // Échec → listes vides, en silence : une pastille absente vaut mieux
      // qu'une page d'accueil en erreur (même parti pris que le listener congés).
      const res = await Promise.allSettled(requetes);
      if (annule) return;
      const lire = (r) => (r.status === "fulfilled" ? r.value.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setTachesAssignees(lire(res[0]));
      // Dédoublonnage par id, INDISPENSABLE : une tâche-chantier que j'ai
      // créée sur mon propre chantier revient par (2) ET par (3), et ne doit
      // compter qu'une fois dans « à valider ».
      const parId = new Map();
      // res[0] = la requête (1) ; tout le reste = les paquets de (2) puis (3).
      res.slice(1).forEach((r) => lire(r).forEach((t) => parId.set(t.id, t)));
      setTachesAValider([...parId.values()]);
    })();
    return () => { annule = true; };
  }, [showTaches, user?.uid, primaryRole, chantiers, rolesConfig]);

  // ─── Interventions : compteurs d'accueil (getDocs PONCTUEL, pas d'onSnapshot) ───
  // MÊME périmètre et mêmes requêtes que la liste (listerInterventions) :
  // superviseur → requête nue ; sinon fiches ouvertes + accesUids.
  const roleInterventions = roleClaim(user);
  const [interventionsAccueil, setInterventionsAccueil] = useState([]);
  useEffect(() => {
    if (!showInterventions || !user?.uid) { setInterventionsAccueil([]); return undefined; }
    let annule = false;
    listerInterventions({ role: roleInterventions, uid: user.uid })
      .then((r) => { if (!annule) setInterventionsAccueil(r.interventions); })
      .catch(() => { if (!annule) setInterventionsAccueil([]); });
    return () => { annule = true; };
  }, [showInterventions, user?.uid, roleInterventions]);
  const notifInterventions = notificationsInterventions({
    interventions: interventionsAccueil, role: roleInterventions, uid: user?.uid,
  });
  const ouvrirIntervention = (unique, onglet) => {
    // Plusieurs fiches : la liste s'ouvre sur l'onglet voulu (mémorisé).
    if (onglet) {
      let stockage = null;
      try { stockage = window.localStorage; } catch { /* stockage bloqué */ }
      memoriserOnglet(stockage, onglet);
    }
    // Une seule fiche : l'ancre la désigne, InterventionsPage l'ouvre.
    if (unique?.id) {
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search
          + `#${ancreAvecId("module:interventions", unique.id)}`);
      } catch { /* la liste s'ouvre simplement */ }
    }
    onOpenModule("interventions");
  };
  // Créneaux à réaffecter (rôles planning) : docs POOL nés d'un « Le retirer
  // du chantier » (champ de tête issuDeIntervention) et pas encore
  // réaffectés (ressourceId nul, filtré côté client). getDocs PONCTUEL,
  // plafonné à 500 (au-delà, le compte affiché est un minimum).
  const [creneauxReaffecter, setCreneauxReaffecter] = useState({ liste: [], tronque: false });
  useEffect(() => {
    if (!peutPlanifier(roleInterventions) || !user?.uid) { setCreneauxReaffecter({ liste: [], tronque: false }); return undefined; }
    let annule = false;
    getDocs(query(collection(db, "planningCreneaux"), where("issuDeIntervention", "!=", null), limit(501)))
      .then((snap) => {
        if (annule) return;
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCreneauxReaffecter({ liste: docs.slice(0, 500), tronque: docs.length > 500 });
      })
      .catch(() => { if (!annule) setCreneauxReaffecter({ liste: [], tronque: false }); });
    return () => { annule = true; };
  }, [roleInterventions, user?.uid]);
  const notifReaffecter = notificationReaffectation(creneauxReaffecter.liste);
  // Planning ouvert sur la semaine du créneau (le plus proche s'il y en a
  // plusieurs) : « #/planning/<AAAA-MM-JJ> », lu par PlanningGrid.
  const ouvrirPlanningReaffecter = (liste) => {
    const dates = (liste || []).map((c) => c?.date).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d || "")).sort();
    const ancre = dates.length ? ancreAvecId("module:planning", dates[0]) : null;
    if (ancre) {
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search + `#${ancre}`);
      } catch { /* la semaine courante s'ouvre simplement */ }
    }
    onOpenModule("planning");
  };
  const libelleCreneauPool = (cr) => {
    const l = (cr?.taches || []).find((t) => t?.chantierId) || {};
    const c = l.chantierId ? (chantiers || []).find((x) => String(x.num || x._id) === String(l.chantierId)) : null;
    const nom = c?.nom || (l.chantierId ? `chantier ${l.chantierId}` : (l.posteLabel || "ligne"));
    const jour = /^\d{4}-\d{2}-\d{2}$/.test(cr?.date || "")
      ? new Date(`${cr.date}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "";
    return `${nom} — ${jour}${cr?.periode === "PM" ? " après-midi" : " matin"}`;
  };

  const badgeInterventions = (notifInterventions.aVenir?.count || 0)
    + (notifInterventions.aPlanifier?.count || 0) + (notifInterventions.aFacturer?.count || 0);

  // ─── Calcul des notifications ───
  const notifications = useMemo(() => {
    // Réserves : retards, à traiter et RDV imminents — module pur, testé.
    // Le périmètre est TOUJOURS nominatif : un Admin/Direction avec
    // view:"all" ne reçoit plus en alerte les réserves des autres.
    const resNotifs = computeReservesNotifications({ reserves, user });

    // v10.E — Notifications COMMANDES filtrées par utilisateur
    //   Avant (v10.D) : un user pilotage (Admin/Direction/Assistante) voyait
    //   TOUTES les commandes de la boîte → bannière "5 commandes à valider"
    //   alors qu'aucune ne le concernait personnellement. Très perturbant.
    //
    //   Maintenant : on filtre TOUJOURS par les chantiers dont la personne
    //   est conducteur (via affectations.conducteurId OU via le legacy
    //   chantier.conducteur == "Prénom Nom"). Les rôles pilotage qui ne sont
    //   pas conducteurs n'ont donc plus de bannières commandes intempestives ;
    //   ils retrouvent la vue d'ensemble dans le Dashboard Direction.
    const safeCommandes = commandes || [];
    const uid = user?._id || user?.id;
    const fullName = `${user?.prenom || ""} ${user?.nom || ""}`.trim();

    // Mes chantiers en tant que conducteur (deux conventions de stockage)
    const isMyChantier = (ch) => {
      if (!ch) return false;
      if (ch.affectations?.conducteurId === uid) return true;
      if (ch.conducteur && ch.conducteur === fullName) return true;
      return false;
    };

    const isMyCommande = (cmd) => {
      if (!cmd?.chantier) return false;
      const monChantier = chantiers.find(c => c.nom === cmd.chantier && isMyChantier(c));
      return !!monChantier;
    };

    // Commandes À VALIDER (statut = "En attente de validation")
    const commandesAValider = safeCommandes.filter(cmd =>
      cmd.statut === "En attente de validation" && isMyCommande(cmd)
    );

    // Commandes VALIDÉES MAIS NON ENVOYÉES (nouveau statut v10.D)
    const commandesAEnvoyer = safeCommandes.filter(cmd =>
      cmd.statut === "Validée" && isMyCommande(cmd)
    );

    // v10.J — Commandes EN RETARD de réception
    // On utilise isOrderLate centralisé :
    //   - exclut En attente / Validée / Refusée / Réceptionnée
    //   - exclut les commandes déjà signées (signatureData posée)
    //   - utilise la date AR fournisseur si OCR activé + datelivraison présente,
    //     sinon retombe sur la date de réception souhaitée
    const commandesEnRetard = safeCommandes.filter(cmd =>
      isMyCommande(cmd) && isOrderLate(cmd, { featureFlags })
    );

    // Nouveaux messages non lus sur une commande qui me concerne (fil
    // OrderMessageThread). myId aligné sur message.userId (= user.id||user._id).
    const myId = user?.id || user?._id || "";
    const hasUnreadMsg = (cmd) => hasUnreadMessages(cmd, myId);
    const iParticipate = (cmd) =>
      cmd.userId === myId ||
      (Array.isArray(cmd.messages) ? cmd.messages : []).some(m => m.userId === myId) ||
      isMyCommande(cmd);
    const commandesNouveauxMessages = myId
      ? safeCommandes.filter(c => iParticipate(c) && hasUnreadMsg(c))
      : [];

    // v10.L — Commandes À ENVOYER DANS ESABORA
    // Affiché uniquement si esaboraEnabled === true
    // Compte les commandes "Envoyée aux achats" pas encore syncées (esaboraStatus !== "synced")
    // v10.L.4 — Bannière restreinte aux rôles "achats" (Admin/Direction/Assistante).
    //           Les autres rôles (conducteur travaux, monteur, etc.) ne la voient pas :
    //           ils ne peuvent pas agir, donc inutile de leur afficher.
    const canSendEsabora = (() => {
      if (!user) return false;
      const roles = Array.isArray(user.roles) ? user.roles
                  : (user.role ? [user.role] : []);
      const fonction = user.fonction || "";
      if (roles.includes("Admin") || fonction === "Admin") return true;
      if (roles.includes("Direction") || fonction === "Direction") return true;
      if (roles.some(r => (r||"").toLowerCase().includes("assist"))) return true;
      if ((fonction||"").toLowerCase().includes("assist")) return true;
      return false;
    })();
    const commandesAEsabora = (featureFlags.esaboraEnabled && canSendEsabora)
      ? safeCommandes.filter(cmd =>
          (cmd.statut === "Envoyée aux achats" || cmd.statut === "Commandée")
          && cmd.esaboraStatus !== "synced"
          && cmd.esaboraStatus !== "partial"
          && isMyCommande(cmd)
        )
      : [];

    // RH-badge — nombre de demandes de congés à valider PAR CE user
    let rhAValider = 0;
    if (validerScope === "own_chantiers") {           // Conducteur = N1
      const ids = new Set(resourcesForConductor(users, chantiers, user).map((r) => r.id));
      rhAValider = congesEnCours.filter(
        (c) => c.statut === "DEMANDE" && !c.sauteN1 && ids.has(c.ressourceId),
      ).length;
    } else if (validerScope === "all") {              // Direction/Assistante = N2
      rhAValider = congesEnCours.filter(
        (c) => c.statut === "VALIDEE_N1" || (c.statut === "DEMANDE" && c.sauteN1),
      ).length;
    }

    // T4 — nominatif : ce qui m'est assigné, et ce qui attend MA validation.
    const tachesNotifs = computeTachesNotifications({ tachesAssignees, tachesAValider, user });

    return {
      "parc-machines": computeParcNotifications({ outillageSorties, user }),
      // Allume la pastille de la tuile « Tâches » (<Tile notif={...}/>).
      "taches": tachesNotifs.count > 0 ? tachesNotifs : null,
      "tachesAValider": tachesNotifs.aValider,
      "rh": rhAValider > 0 ? { count: rhAValider } : null,
      "interventions": badgeInterventions > 0 ? { count: badgeInterventions } : null,
      "avancement": computeAvancementNotifications({
        avancementValidations, chantiers, user,
      }),
      "reserves": resNotifs.retards.length > 0
        ? { count: resNotifs.retards.length, label: `${resNotifs.retards.length} en retard` }
        : null,
      "reservesATraiter": resNotifs.aTraiter.length > 0
        ? { count: resNotifs.aTraiter.length }
        : null,
      "rdvImminents": resNotifs.rdvImminents.length > 0
        ? { count: resNotifs.rdvImminents.length, items: resNotifs.rdvImminents }
        : null,
      "commandesAValider": commandesAValider.length > 0
        ? { count: commandesAValider.length }
        : null,
      "commandesAEnvoyer": commandesAEnvoyer.length > 0
        ? { count: commandesAEnvoyer.length }
        : null,
      "commandesEnRetard": commandesEnRetard.length > 0
        ? { count: commandesEnRetard.length }
        : null,
      "commandesAEsabora": commandesAEsabora.length > 0
        ? { count: commandesAEsabora.length }
        : null,
      "commandesMessages": commandesNouveauxMessages.length > 0
        ? { count: commandesNouveauxMessages.length, items: commandesNouveauxMessages }
        : null,
      // Allume le badge de la tuile "commandes" (pas d'autre agrégateur existant)
      "commandes": commandesNouveauxMessages.length > 0
        ? { count: commandesNouveauxMessages.length }
        : null,
    };
  }, [outillageSorties, avancementValidations, chantiers, user, reserves, commandes, rolesConfig, featureFlags, congesEnCours, users, validerScope, tachesAssignees, tachesAValider, badgeInterventions]);

  const showRappelAvancement = isRappelAvancementActif()
    && (notifications.avancement?.count || 0) > 0;

  return (
    <div style={{ paddingTop: 16, paddingBottom: 20 }}>
      {/* Accroche éditoriale */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontFamily: font.display, fontSize: 26, fontWeight: fontWeight.regular,
          color: EPJ.gray900, letterSpacing: "-0.02em", lineHeight: 1.1,
        }}>
          Bonjour, <span style={{ fontStyle: "italic" }}>{user.prenom}</span>.
        </div>
        <div style={{
          fontSize: fontSize.sm, color: EPJ.gray500, marginTop: space.xs, fontWeight: fontWeight.regular,
        }}>
          Que souhaitez-vous faire aujourd'hui&nbsp;?
        </div>
      </div>

      {/* Bannière rappel outils en retard */}
      {canParc && (notifications["parc-machines"]?.count || 0) > 0 && (
        <Banner
          tone="danger"
          icon="⏰"
          title={`${notifications["parc-machines"].count} outil${notifications["parc-machines"].count > 1 ? "s" : ""} en retard`}
          text="Tape ici pour voir la liste et envoyer les SMS de rappel."
          onClick={() => onOpenModule("parc-machines")}
        />
      )}

      {/* v10.D — Bannière COMMANDES À VALIDER (conducteur / admin) */}
      {(notifications.commandesAValider?.count || 0) > 0 && (
        <Banner
          tone="warning"
          icon="⏳"
          title={`${notifications.commandesAValider.count} commande${notifications.commandesAValider.count > 1 ? "s" : ""} à valider`}
          text="Un monteur attend ta validation — tape ici."
          onClick={() => onOpenModule("commandes")}
        />
      )}

      {/* v10.D — Bannière COMMANDES VALIDÉES EN ATTENTE D'ENVOI */}
      {(notifications.commandesAEnvoyer?.count || 0) > 0 && (
        <Banner
          tone="info"
          icon="📤"
          title={`${notifications.commandesAEnvoyer.count} commande${notifications.commandesAEnvoyer.count > 1 ? "s" : ""} à envoyer`}
          text="Commandes validées mais pas encore envoyées aux achats."
          onClick={() => onOpenModule("commandes")}
        />
      )}

      {/* v10.D — Bannière COMMANDES EN RETARD de réception */}
      {(notifications.commandesEnRetard?.count || 0) > 0 && (
        <Banner
          tone="danger"
          icon="⏰"
          title={`${notifications.commandesEnRetard.count} commande${notifications.commandesEnRetard.count > 1 ? "s" : ""} en retard de livraison`}
          text="La date de réception prévue est dépassée."
          onClick={() => onOpenModule("commandes")}
        />
      )}

      {/* Bannière NOUVEAUX MESSAGES sur mes commandes (fil de discussion) */}
      {(notifications.commandesMessages?.count || 0) > 0 && (
        <Banner
          tone="info"
          icon="💬"
          title={`${notifications.commandesMessages.count} nouveau${notifications.commandesMessages.count > 1 ? "x" : ""} message${notifications.commandesMessages.count > 1 ? "s" : ""} sur vos commandes`}
          text="Quelqu'un a répondu — tape ici pour voir."
          onClick={() => {
            const cmds = notifications.commandesMessages.items || [];
            onOpenModule("commandes", cmds.length === 1 ? { orderId: cmds[0]._id } : undefined);
          }}
        />
      )}

      {/* v10.L — Bannière COMMANDES À ENVOYER DANS ESABORA */}
      {(notifications.commandesAEsabora?.count || 0) > 0 && (
        <Banner
          tone="info"
          icon="🔗"
          title={`${notifications.commandesAEsabora.count} commande${notifications.commandesAEsabora.count > 1 ? "s" : ""} à envoyer dans Esabora`}
          text="Pas encore synchronisée avec l'ERP."
          onClick={() => onOpenModule("commandes")}
        />
      )}

      {/* v10.C — Bannière rappel RDV imminents (priorité haute) */}
      {(notifications.rdvImminents?.count || 0) > 0 && (
        <Banner
          tone="warning"
          icon="📅"
          title={`${notifications.rdvImminents.count} RDV prévu${notifications.rdvImminents.count > 1 ? "s" : ""} dans les 48 h`}
          text="Tape ici pour voir tes prochains rendez-vous d'intervention."
          onClick={() => onOpenModule("reserves")}
        />
      )}

      {/* v10.C — Bannière "réserves à traiter" (attribuées, pas en retard) */}
      {(notifications.reservesATraiter?.count || 0) > 0 && (
        <Banner
          tone="info"
          icon="📝"
          title={`${notifications.reservesATraiter.count} réserve${notifications.reservesATraiter.count > 1 ? "s" : ""} à traiter`}
          text="Tape ici pour voir tes réserves attribuées à planifier ou à lever."
          onClick={() => onOpenModule("reserves")}
        />
      )}

      {/* Bannière rappel réserves en retard */}
      {(notifications.reserves?.count || 0) > 0 && (
        <Banner
          tone="danger"
          icon="📝"
          title={`${notifications.reserves.count} réserve${notifications.reserves.count > 1 ? "s" : ""} en retard`}
          text="Tape ici pour voir les réserves non traitées dans les délais."
          onClick={() => onOpenModule("reserves")}
        />
      )}

      {/* Bannière rappel avancement du mois */}
      {showRappelAvancement && (
        <Banner
          tone="danger"
          icon="⏰"
          title={`Avancement de ${currentMonthLabel()} à remplir`}
          text={`${notifications.avancement.label} — tape ici pour accéder au module.`}
          onClick={() => onOpenModule("avancement")}
        />
      )}

      {/* RH-badge — Bannière "congés à valider" (conducteur N1 / direction N2) */}
      {(notifications.rh?.count || 0) > 0 && (
        <Banner
          tone="info"
          icon="🌴"
          title={`${notifications.rh.count} demande${notifications.rh.count > 1 ? "s" : ""} de congés à valider`}
          text="Tape ici pour ouvrir la file de validation."
          onClick={() => onOpenModule("rh")}
        />
      )}

      {/* T4 — Bannière "tâches qui me sont attribuées" */}
      {(notifications.taches?.count || 0) > 0 && (
        <Banner
          tone="info"
          icon="✅"
          title={notifications.taches.label}
          text="Tape ici pour ouvrir tes tâches."
          onClick={() => onOpenModule("taches")}
        />
      )}

      {/* T4 — Bannière "tâches à valider" (pilotes).
          T16 — UNE seule tâche : la bannière la NOMME (titre + chantier) et le
          clic y mène (focus lu par TachesPage). À partir de deux, texte
          générique : on ne nomme pas une liste. */}
      {(notifications.tachesAValider?.count || 0) > 0 && (
        <Banner
          tone="warning"
          icon="🔎"
          title={notifications.tachesAValider.label}
          text={notifications.tachesAValider.tache
            ? `« ${notifications.tachesAValider.tache.titre} » — ${nomChantierTache(chantiers, notifications.tachesAValider.tache.chantierId)}`
            : "Quelqu'un a coché « fait » — tape ici pour valider."}
          onClick={() => {
            const unique = notifications.tachesAValider.tache;
            if (unique?.id) poserFocus(stockageNavigateur("sessionStorage"), unique.id);
            onOpenModule("taches");
          }}
        />
      )}

      {/* Interventions — à planifier (fiches ouvertes, tous les rôles du
          module, lien vers l'onglet « À planifier ») ; à facturer ; terrain :
          à venir. Une seule fiche : la bannière la NOMME et le clic y mène. */}
      {[
        { n: notifInterventions.aPlanifier, onglet: ONGLET_A_PLANIFIER, tone: "warning", icon: "🛠️", titre: (c) => `${c} intervention${c > 1 ? "s" : ""} à planifier` },
        { n: notifInterventions.aFacturer, tone: "info", icon: "🧾", titre: (c) => `${c} intervention${c > 1 ? "s" : ""} réalisée${c > 1 ? "s" : ""} à facturer` },
        { n: notifInterventions.aVenir, tone: "info", icon: "🛠️", titre: (c) => `${c} intervention${c > 1 ? "s" : ""} à venir` },
      ].filter((b) => b.n).map((b) => (
        <Banner
          key={b.titre(1)}
          tone={b.tone}
          icon={b.icon}
          title={b.titre(b.n.count)}
          text={b.n.unique
            ? `« ${b.n.unique.travaux || ""} » — ${b.n.unique.client?.nom || ""}${b.n.unique.numIntervention ? ` (${b.n.unique.numIntervention})` : ""}`
            : "Tape ici pour ouvrir les interventions."}
          onClick={() => ouvrirIntervention(b.n.unique, b.onglet)}
        />
      ))}

      {/* Planning — créneaux retirés d'un chantier pour une intervention,
          à réaffecter. Un seul : nommé (chantier, date). Clic → planning. */}
      {notifReaffecter && (
        <Banner
          tone="warning"
          icon="🔁"
          title={`${notifReaffecter.count}${creneauxReaffecter.tronque ? "+" : ""} créneau${notifReaffecter.count > 1 ? "x" : ""} à réaffecter`}
          text={notifReaffecter.unique
            ? `${libelleCreneauPool(notifReaffecter.unique)} : tape ici pour ouvrir le planning.`
            : "Lignes de chantier retirées pour une intervention — tape ici pour les réaffecter dans le planning."}
          onClick={() => ouvrirPlanningReaffecter(creneauxReaffecter.liste)}
        />
      )}

      {/* Grille de tuiles — PWA : 2 colonnes (inchangé) ; desktop : grille
          aérée auto-fill dans le cadre 1320 */}
      {mesRemises.length > 0 && (
        <Banner tone={mesRemises.some(d => etatEcheance(d, horlogeRappel).niveau === "DEPASSEE") ? "danger" : "warning"}
          icon="⏰" title={`${mesRemises.length} offre(s) à remettre ou vérifier`}
          text={`${mesRemises[0].nom} — ${etatEcheance(mesRemises[0], horlogeRappel).label}`}
          onClick={() => onOpenModule("chiffrage")} />
      )}
      {curatedTiles.length > 0 ? (
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fill, minmax(240px, 1fr))",
          gap: space.md,
        }}>
          {curatedTiles.map((tile, i) => (
            <Tile
              key={tile.id}
              meta={tile}
              notif={tile.id === "chiffrage" ? { count: mesRemises.length, label: "Remises à suivre" } : notifications[tile.id]}
              onClick={() => {
                if (!tile.enabled) return;
                if (tile.id === "dashboard") onOpenDashboard(user);
                else if (tile.id === "collection-dashboards") onOpenCollectionDashboards();
                else onOpenModule(tile.id);
              }}
              isFullWidth={isMobile && curatedTiles.length % 2 === 1 && i === curatedTiles.length - 1}
            />
          ))}
        </div>
      ) : (
        <div style={{
          background: EPJ.gray50, border: `1px solid ${EPJ.gray200}`,
          borderRadius: radius.lg, padding: space.lg, textAlign: "center",
          fontSize: fontSize.sm, color: EPJ.gray500,
        }}>
          Aucun module ne vous est accessible pour l'instant. Contactez votre administrateur.
        </div>
      )}
    </div>
  );
}

// ─── Tuile avec badge ─────────────────────────────────────────
function Tile({ meta, notif, onClick, isFullWidth }) {
  const accent = meta.accent;
  const hasNotif = notif && notif.count > 0;
  return (
    <div
      className="epj-tile"
      onClick={onClick}
      role="button"
      tabIndex={meta.enabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (!meta.enabled) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
      }}
      style={{
        "--accent": accent,
        "--accent-soft": `${accent}1A`,
        gridColumn: isFullWidth ? "1 / -1" : undefined,
        opacity: meta.enabled ? 1 : 0.55,
        cursor: meta.enabled ? "pointer" : "not-allowed",
        position: "relative",
      }}
    >
      {/* Badge notification (coin haut droit) */}
      {hasNotif && (
        <div style={{
          position: "absolute", top: space.md, right: space.md,
          background: EPJ.red, color: EPJ.white,
          minWidth: 22, height: 22, borderRadius: radius.pill,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: fontSize.xs, fontWeight: fontWeight.semibold,
          fontVariantNumeric: "tabular-nums",
          padding: `0 ${space.xs}px`,
          boxShadow: `0 0 0 3px ${EPJ.red}30`,
          zIndex: 2,
        }}>{notif.count}</div>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div className="epj-tile-icon">{meta.icon}</div>
        {!meta.enabled && <Badge tone="neutral" label="Bientôt" />}
      </div>
      <div>
        <div style={{
          fontWeight: fontWeight.semibold, fontSize: fontSize.base, color: EPJ.gray900,
          letterSpacing: "-0.01em", lineHeight: 1.2,
        }}>
          {meta.title}
        </div>
        <div style={{ fontSize: fontSize.xs, color: EPJ.gray500, marginTop: space.xs / 2 }}>
          {meta.subtitle}
        </div>
        {hasNotif && notif.label && (
          <div style={{
            fontSize: fontSize.xs, color: EPJ.red, fontWeight: fontWeight.medium,
            marginTop: space.xs, lineHeight: 1.3,
          }}>
            ⚠ {notif.label}
          </div>
        )}
      </div>
    </div>
  );
}

// T16 — nom du chantier d'une tâche pour la bannière « 1 tâche à valider ».
// Résolu ICI (notificationsUtils n'a pas le référentiel). Chantier absent du
// DataContext (archivé, hors périmètre) → le numéro seul.
function nomChantierTache(chantiers, chantierId) {
  if (!chantierId) return "sans chantier";
  const c = (chantiers || []).find((x) => String(x.num || x._id) === String(chantierId));
  return c?.nom || String(chantierId);
}
